import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Wand2,
  Sparkles,
  HelpCircle,
  Zap,
  Layers,
  Swords,
  Heart,
  Coins,
  Target
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AdvancedStrategyOptions } from "./AdvancedStrategyOptions";

interface SimplifiedStrategyStepProps {
  themes: string;
  setThemes: (themes: string) => void;
  cardTypes: string[];
  setCardTypes: (types: string[]) => void;
  creatureTypes: string[];
  setCreatureTypes: (types: string[]) => void;
  isGuided: boolean;
  setColorIdentity: (colors: string[]) => void;
  handleNextStep: (strategyOverride?: string, colorIdentityOverride?: string[], themesOverride?: string, creatureTypeOverride?: string, cardTypeOverride?: string) => void;
  setSelectedStrategy: (strategy: string) => void;
}

const STRATEGY_OPTIONS = [
  {
    id: "aggressive",
    name: "Aggressive",
    icon: <Swords className="w-6 h-6" />,
    description: "Trample opponents with big creatures",
    themes: "combat step, attack, haste, trample, menace, double, triple, power>=5",
    color: "red",
    colorIdentity: ['R', 'G']
  },
  {
    id: "typal",
    name: "Typal",
    icon: <Layers className="w-6 h-6" />,
    description: "Focus on a specific card type",
    themes: "",
    color: "blue",
    colorIdentity: ['W', 'U', 'B', 'R', 'G']
  },
  {
    id: "kindred",
    name: "Kindred",
    icon: <Zap className="w-6 h-6" />,
    description: "Unleash the power of your favorite creature type",
    themes: "",
    color: "purple",
    colorIdentity: ['W', 'U', 'B', 'R', 'G']
  },
  {
    id: "lifegain",
    name: "Lifegain",
    icon: <Heart className="w-6 h-6" />,
    description: "Gain life and outlast opponents",
    themes: "/(whenever you gain life|gain(?:ed)? d+ life|gain that much life|you have d+ or more life)/",
    color: "green",
    colorIdentity: ['W', 'B']
  },
  {
    id: "tokens",
    name: "Token Army",
    icon: <Target className="w-6 h-6" />,
    description: "Create lots of creature tokens",
    themes: "each token you control, tokens you control, creature token, convoke, populate",
    color: "amber",
    colorIdentity: ['G', 'W']
  },
  {
    id: "treasure",
    name: "Treasure",
    icon: <Coins className="w-6 h-6" />,
    description: "Generate treasures and big spells",
    themes: "treasure, artifact you control, artifacts you control, /whenever you (create|sacrifice|create or sacrifice) a token/",
    color: "yellow",
    colorIdentity: ['R', 'B']
  }
];

export const SimplifiedStrategyStep = ({
  themes,
  setThemes,
  cardTypes,
  setCardTypes,
  creatureTypes,
  setCreatureTypes,
  isGuided,
  setColorIdentity,
  handleNextStep,
  setSelectedStrategy
}: SimplifiedStrategyStepProps) => {
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>("");

  const handleStrategySelect = (strategyId: string) => {
    const strategy = STRATEGY_OPTIONS.find(s => s.id === strategyId);
    if (strategy) {
      setSelectedStrategyId(strategyId);
      setSelectedStrategy(strategyId);
      setThemes(strategy.themes);

      // Clear kindred data when selecting non-kindred strategies
      if (strategyId !== 'kindred') {
        setCreatureTypes([]);
      }

      if (isGuided) {
        setColorIdentity(strategy.colorIdentity);
        handleNextStep(strategy.id, strategy.colorIdentity, strategy.themes);
      }
    }
  };

  const selectedStrategyData = STRATEGY_OPTIONS.find(s => s.id === selectedStrategyId);

  return (
    <div className="space-y-4 md:space-y-8">
      {/* Simplified Hero Section */}
      <div className="flex items-center justify-center gap-2">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
          <Wand2 className="w-6 h-6 md:w-8 md:h-8 text-primary" />
        </div>
        <div className="text-left">
          <h2 className="text-lg md:text-xl font-bold">Choose Your Strategy</h2>
          <p className="text-muted-foreground text-xs md:text-sm max-w-md">
            How do you want to win? Pick a strategy that sounds fun to you!
          </p>
        </div>
      </div>

      {/* Strategy Selection */}
      <Card>
        <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
          <div className="flex items-center justify-center gap-2 mb-1 md:mb-2">
            <Label className="font-medium text-sm md:text-base">Pick Your Playstyle</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Each strategy focuses on a different way to play and win. Don't worry - you can mix strategies later!</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            {STRATEGY_OPTIONS.map((strategy) => (
              <Card
                key={strategy.id}
                className={`cursor-pointer transition-all duration-200 hover:scale-105 touch-manipulation ${
                  selectedStrategyId === strategy.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleStrategySelect(strategy.id)}
              >
                <CardContent className="p-2 md:p-3 text-center space-y-1 md:space-y-2">
                  <div className={`w-8 h-8 md:w-10 md:h-10 mx-auto rounded-full flex items-center justify-center ${
                    selectedStrategyId === strategy.id
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <div className="w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                      {strategy.icon}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 leading-tight">{strategy.name}</h3>
                    <p className="text-xs text-muted-foreground leading-tight">{strategy.description}</p>
                  </div>

                  {selectedStrategyId === strategy.id && (
                    <div className="flex items-center justify-center gap-1 text-primary">
                      <Sparkles className="w-3 h-3" />
                      <span className="text-xs font-medium">Selected</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedStrategyData && (
            <div className="p-2 md:p-3 bg-primary/5 rounded-lg text-center">
              <div className="flex items-center justify-center gap-1 md:gap-2 mb-1">
                <span className="font-medium text-primary text-xs md:text-sm">{selectedStrategyData.name} Strategy Selected</span>
              </div>
              <p className="text-xs text-muted-foreground">
                We'll find cards that work great with this strategy!
              </p>
            </div>
          )}

          {!selectedStrategyData && (
            <div className="text-center p-3 md:p-4 border-2 border-dashed border-muted rounded-lg">
              <div className="space-y-1">
                <div className="text-muted-foreground text-xs md:text-sm">Click on a strategy above to get started</div>
                <div className="text-xs text-muted-foreground">
                  New to Magic? Try "Aggressive" or "Token Army" - they're beginner-friendly!
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Options */}
      {!isGuided && (
        <AdvancedStrategyOptions
          themes={themes}
          setThemes={setThemes}
          cardTypes={cardTypes}
          setCardTypes={setCardTypes}
          creatureTypes={creatureTypes}
          setCreatureTypes={setCreatureTypes}
        />
      )}
    </div>
  );
};